import { useState, useEffect, useCallback } from 'react';
import { useGalleryApprovals } from '@/features/gallery/hooks/useGalleryApprovals';
import { useImageApprovals } from './useImageApprovals';
import { useToast } from '@/components/ui/use-toast';
import type { ApprovalItem } from '../types';
import type { GalleryAllImage } from '@/lib/supabase';

export interface ApprovalQueueHook {
  pendingGalleries: ApprovalItem[];
  pendingImages: GalleryAllImage[];
  isLoading: boolean;
  galleriesLoading: boolean;
  imagesLoading: boolean;
  error: string | null;
  refreshQueue: () => Promise<void>;
  approveGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  rejectGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  flagGallery: (galleryId: number, reason?: string) => Promise<boolean>;
  approveImage: (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ) => Promise<boolean>;
  rejectImage: (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ) => Promise<boolean>;
  flagImage: (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ) => Promise<boolean>;
}

export const useApprovalQueue = (): ApprovalQueueHook => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const {
    pendingGalleries,
    isLoading: galleriesLoading,
    error: galleriesError,
    fetchPendingGalleries,
    approveGallery,
    rejectGallery,
    flagGallery,
  } = useGalleryApprovals();

  const {
    pendingImages,
    isLoading: imagesLoading,
    error: imagesError,
    fetchPendingImages,
    approveImage,
    rejectImage,
    flagImage,
  } = useImageApprovals();

  const refreshQueue = useCallback(async (): Promise<void> => {
    try {
      const shouldLog = import.meta.env.MODE !== 'production' && import.meta.env.MODE !== 'test'
      if (shouldLog) {
        console.log('Refreshing approval queue...');
      }

      await Promise.all([fetchPendingGalleries(), fetchPendingImages()]);

      if (shouldLog) {
        console.log('Approval queue refreshed successfully');
      }
    } catch (error) {
      if (import.meta.env.MODE !== 'production' && import.meta.env.MODE !== 'test') {
        console.error('Error refreshing approval queue:', error);
      }
      toast({
        title: 'Error',
        description: 'Failed to refresh approval queue',
        variant: 'destructive',
      });
    }
  }, [fetchPendingGalleries, fetchPendingImages]);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  useEffect(() => {
    setIsLoading(galleriesLoading || imagesLoading);
  }, [galleriesLoading, imagesLoading]);

  useEffect(() => {
    const combinedError = galleriesError || imagesError;
    setError(combinedError);
  }, [galleriesError, imagesError]);

  const handleApproveGallery = async (galleryId: number, reason?: string): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await approveGallery(galleryId, reason);
    }
    return await approveGallery(galleryId);
  };

  const handleRejectGallery = async (galleryId: number, reason?: string): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await rejectGallery(galleryId, reason);
    }
    return await rejectGallery(galleryId);
  };

  const handleFlagGallery = async (galleryId: number, reason?: string): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await flagGallery(galleryId, reason);
    }
    return await flagGallery(galleryId);
  };

  const handleApproveImage = async (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await approveImage(imageId, imageType, reason);
    }
    return await approveImage(imageId, imageType);
  };

  const handleRejectImage = async (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await rejectImage(imageId, imageType, reason);
    }
    return await rejectImage(imageId, imageType);
  };

  const handleFlagImage = async (
    imageId: number,
    imageType?: 'gallery_photo' | 'job_image',
    reason?: string
  ): Promise<boolean> => {
    if (typeof reason === 'string') {
      return await flagImage(imageId, imageType, reason);
    }
    return await flagImage(imageId, imageType);
  };

  return {
    pendingGalleries,
    pendingImages,
    isLoading,
    galleriesLoading,
    imagesLoading,
    error,
    refreshQueue,
    approveGallery: handleApproveGallery,
    rejectGallery: handleRejectGallery,
    flagGallery: handleFlagGallery,
    approveImage: handleApproveImage,
    rejectImage: handleRejectImage,
    flagImage: handleFlagImage,
  };
};
